import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ArrowLeft, Edit, Mail, Loader2, Key } from 'lucide-react';
import { Student } from '@/types';
import { studentApi } from '@/lib/api';
import { useToast } from '@/components/ui/use-toast';
import { StatusBadge } from '@/components/ui/status-badge';

const StudentDetailPage = () => {
  const { studentId } = useParams<{ studentId: string }>();
  const [student, setStudent] = useState<Student | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const fetchStudent = async () => {
      try {
        if (!studentId) {
          setError('Student ID is missing');
          return;
        }

        const data = await studentApi.getById(studentId);
        setStudent(data);
      } catch (err) {
        console.error('Error fetching student:', err);
        setError('Failed to load student data. Please try again.');
        toast({
          title: 'Error',
          description: 'Failed to load student data',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchStudent();
  }, [studentId, toast]);

  const handleResendWelcomeEmail = async () => {
    if (!studentId) return;

    setSendingEmail(true);
    try {
      const response = await studentApi.resendWelcomeEmail(studentId);

      if (response.success) {
        toast({
          title: 'Success',
          description: response.message,
        });
      } else {
        toast({
          title: 'Error',
          description: response.message,
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Error resending welcome email:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to resend welcome email. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSendingEmail(false);
    }
  };

  const handleResetPassword = async () => {
    if (!studentId) return;

    if (!confirm('Are you sure you want to reset this student\'s password? A password reset email will be sent to the student.')) {
      return;
    }

    setResettingPassword(true);
    try {
      const response = await studentApi.resetPassword(studentId);

      if (response.success) {
        toast({
          title: 'Success',
          description: response.message,
        });
      } else {
        toast({
          title: 'Error',
          description: response.message,
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Error resetting password:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to reset password. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setResettingPassword(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" asChild>
          <Link to="/students">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <h1 className="text-3xl font-bold tracking-tight">Student Details</h1>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading student details...</p>
          </div>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-12">
          <p className="text-destructive mb-4">{error}</p>
          <Button asChild>
            <Link to="/students">Back to Students</Link>
          </Button>
        </div>
      ) : student ? (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle>Student Information</CardTitle>
            <TooltipProvider>
              <div className="flex gap-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      onClick={handleResendWelcomeEmail}
                      disabled={sendingEmail}
                    >
                      {sendingEmail ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Mail className="mr-2 h-4 w-4" />
                          Resend Welcome Email
                        </>
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Send a new welcome email with setup instructions</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      onClick={handleResetPassword}
                      disabled={resettingPassword}
                      className="text-purple-600 border-purple-600 hover:bg-purple-50"
                    >
                      {resettingPassword ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Resetting...
                        </>
                      ) : (
                        <>
                          <Key className="mr-2 h-4 w-4" />
                          Reset Password
                        </>
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Send a password reset email to the student</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button asChild>
                      <Link to={`/students/${studentId}/edit`}>
                        <Edit className="mr-2 h-4 w-4" />
                        Edit Student
                      </Link>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Edit student information</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </TooltipProvider>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">Name</h3>
                <p className="text-lg">{student.firstName} {student.lastName}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">Student ID</h3>
                <p className="text-lg">{student.studentId}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">Email</h3>
                <p className="text-lg">{student.email}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">Enrollment Date</h3>
                <p className="text-lg">{student.enrollmentDate}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">Status</h3>
                <div className="mt-1">
                  <StatusBadge status={student.status} variant="rounded" />
                </div>
              </div>
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">Batch ID</h3>
                <p className="text-lg">{student.academicYearId}</p>
              </div>
            </div>

          <div className="mt-8">
            <h3 className="text-lg font-medium mb-4">Enrolled Classes</h3>
            {!student.classIds || student.classIds.length === 0 ? (
              <p className="text-muted-foreground">Not enrolled in any classes.</p>
            ) : (
              <ul className="space-y-2">
                {student.classIds.map((classId) => (
                  <li key={classId} className="flex items-center justify-between p-3 bg-muted rounded-md">
                    <span>Class ID: {classId}</span>
                    <Button variant="ghost" size="sm" asChild>
                      <Link to={`/classes/${classId}`}>View</Link>
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </CardContent>
      </Card>
      ) : null}
    </div>
  );
};

export default StudentDetailPage;
